import {
  ArrowBackIos,
  ArrowForward,
  ArrowForwardIos,
  Cancel,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import * as MuiMaterial from "@mui/material";
import dayjs from "dayjs";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import usePrimaryColor from "../../../hooks/usePrimaryColor";
import useUser from "../../../hooks/useUser";
import { useIsMobile } from "../../../utils/Functions";
import * as WORKING_HOURS from "../utils/WorkingHoursRepositroy";
import SimpleAlert from "../../../components/display/SimpleAlert";
import { useFormContext } from "react-hook-form";

export const isEmptyObject = (obj) => {
  return obj && JSON.stringify(obj) === "{}";
};

export default function TimeSlots({
  userSelectedDate,
  setValue,
  warningMessage = null,
  clearErrors,
  register,
  errors,
  availableHours,
  tag,
  handleStartTimeChange,
  userSelectedStartTime,
  workingHours,
  placeholder = "TimeOfAppointment",
  selectedService,
  bookedHours,
  isLoading,
  fetchError,
  testId,
}) {
  const { roles, currentClinic } = useUser();
  const [openTimeSlots, setOpenTimeSlots] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const inputRef = useRef(null);
  const hasValue = inputRef.current?.value;
  const { t } = useTranslation();
  const isEndTime = tag === "endTime";
  const isEndTimeDisabled = isEndTime ? getEndTimeBoolean() : false;
  const color = usePrimaryColor();
  const [page, setPage] = useState(0);

  const formContext = useFormContext();
  const fieldValue = formContext.watch(tag); // Either  "time" or "endTime"

  const groupTimeSlots = (times = []) => {
    return {
      Morning: times.filter((t) => t >= "06:00" && t < "12:00"),
      Afternoon: times.filter((t) => t >= "12:00" && t < "18:00"),
      Evening: times.filter((t) => t >= "18:00"),
      Night: times.filter((t) => t < "06:00"),
    };
  };

  const groupTimeSlotsFiltered = (times = []) => {
    const filtered = times.filter(
      (time) =>
        !WORKING_HOURS.checkIfHourAvailable(
          time,
          bookedHours,
          userSelectedDate,
        ),
    );
    return groupTimeSlots(filtered);
  };

  React.useEffect(() => {
    if (isEndTime) {
      setDisabled(getEndTimeBoolean());
    } else {
      setDisabled(getIsDisabledValue());
    }
  }, [isEndTime, userSelectedStartTime, userSelectedDate, selectedService]);

  function getIsDisabledValue() {
    if (roles.adminOrEmployee || roles.master) {
      return !userSelectedDate;
    } else if (roles.user && currentClinic.basicTier) {
       return !userSelectedDate;
    } else {
      return !userSelectedDate || !selectedService;
    }
  }
  function getEndTimeBoolean() {
    return !userSelectedDate || !userSelectedStartTime;
  }

  const timePickerStyle = useIsMobile()
    ? { mb: 1, ml: 0.5, pr: 0.5, pl: 0.5 }
    : { m: 0.5, pr: 0.5, pl: 0.5 };

  function handleSetTime(time) {
    if (handleStartTimeChange) {
      handleStartTimeChange(time);
    }
    setValue(tag, time);
    clearErrors([tag]);
  }

  const handleOpenTimeSlots = () => {
    setOpenTimeSlots(true);
  };

  const handleCloseTimeSlots = () => {
    setOpenTimeSlots(false);
  };

  const chooseDifferentTime = () => {
    setValue(tag, null);
    handleCloseTimeSlots();
  };

  function isTimeAvailable() {
    const hasHours = availableHours && availableHours.length > 0;
    if (roles.adminOrEmployee) {
      return hasHours;
    }
    if (isEmptyObject(workingHours)) {
      return false;
    }
    return hasHours;
  }

  const groupedSlots = groupTimeSlots(availableHours);

  const filteredTimeSlots = groupTimeSlotsFiltered(availableHours);
  React.useEffect(() => {
    // Reset to the first available category when data changes
    setPage(0);
  }, [userSelectedDate, userSelectedStartTime, availableHours]);

  const keys = Object.keys(groupedSlots).filter(
    (key) => groupedSlots[key].length > 0,
  );
  const totalPages = keys.length;
  const isTimeAvailableConst = isTimeAvailable();

  return (
    <React.Fragment>
      <MuiMaterial.TextField
        fullWidth
        label={t(placeholder)}
        {...register(tag, {
          required: {
            value: !isEndTime,
            message: t("ChooseAppointmentTime"),
          },
        })}
        error={!!errors?.[tag]}
        helperText={errors?.[tag]?.message}
        disabled={disabled}
        onClick={handleOpenTimeSlots}
        inputRef={inputRef}
        InputProps={{
          readOnly: true,
        }}
        inputProps={{ ...(testId && { "data-testid": testId }) }}
        InputLabelProps={{
          shrink: !!fieldValue,
        }}
        sx={{
          input: {
            color: errors?.[tag] ? "#f44336" : undefined,
            "&::placeholder": {
              opacity: errors?.[tag] ? 1 : undefined,
            },
          },
        }}
      />
      <MuiMaterial.Dialog
        open={openTimeSlots}
        onClose={handleCloseTimeSlots}
        fullWidth
        maxWidth={roles.adminOrEmployee ? "md" : "sm"}
        PaperProps={{ "data-testid": testId && `${testId}.dialog` }}
      >
        <MuiMaterial.DialogTitle
          sx={{ textAlign: "center", fontSize: "1.5rem" }}
        >
          {dayjs(userSelectedDate).format("DD/MM/YYYY")}
          {warningMessage ? (
            <SimpleAlert message={warningMessage} severity={"warning"} />
          ) : null}
        </MuiMaterial.DialogTitle>
        <MuiMaterial.DialogContent>
          {isLoading ? (
            <MuiMaterial.LinearProgress />
          ) : fetchError ? (
            <SimpleAlert
              message={"ErrorFetchingAvailableTimeSlots"}
              severity="error"
            />
          ) : (
            <>
              {!isTimeAvailableConst ? (
                <MuiMaterial.Alert
                  severity="warning"
                  data-testid={testId && `${testId}.no-slots-alert`}
                >
                  {t("NoTimeAvailableForThisDate")}
                </MuiMaterial.Alert>
              ) : (
                totalPages > 0 &&
                keys[page] && (
                  <div>
                    <MuiMaterial.Typography
                      variant="subtitle1"
                      fontSize={"1.2rem"}
                      fontWeight={"bold"}
                      sx={{ mt: 2, mb: 1 }}
                    >
                      {t(keys[page])}
                    </MuiMaterial.Typography>
                    <MuiMaterial.Grid container spacing={1}>
                      {groupedSlots[keys[page]]?.map((time, index) => (
                        <MuiMaterial.Grid item key={index} xs={4} sm={3} md={2}>
                          <MuiMaterial.ToggleButton
                            fullWidth
                            data-testid={testId && `${testId}.slot-${time}`}
                            disabled={WORKING_HOURS.checkIfHourAvailable(
                              time,
                              bookedHours,
                              userSelectedDate,
                            )}
                            variant="contained"
                            onClick={() => {
                              handleSetTime(time);
                              handleCloseTimeSlots();
                            }}
                            sx={{
                              ...timePickerStyle,
                              bgcolor: color,
                              color: "white",
                              ":disabled": {
                                bgcolor: "gainsboro",
                                color: "gray",
                              },
                              ":hover": {
                                color: color,
                                border: `1px solid ${color}`,
                              },
                            }}
                          >
                            {time}
                          </MuiMaterial.ToggleButton>
                        </MuiMaterial.Grid>
                      ))}
                    </MuiMaterial.Grid>
                  </div>
                )
              )}

              {isTimeAvailableConst ? (
                <MuiMaterial.Typography textAlign={"center"}>
                  {page + 1} / {totalPages}
                </MuiMaterial.Typography>
              ) : null}
            </>
          )}
        </MuiMaterial.DialogContent>
        <MuiMaterial.DialogActions>
          {isTimeAvailableConst && (
            <>
              <MuiMaterial.IconButton
                size="large"
                color="primary"
                disabled={page === 0}
                data-testid={testId && `${testId}.prev-page-button`}
                onClick={() => setPage(page - 1)}
              >
                <ArrowBackIos />
              </MuiMaterial.IconButton>
              <MuiMaterial.IconButton
                size="large"
                color="primary"
                disabled={page === totalPages - 1}
                data-testid={testId && `${testId}.next-page-button`}
                onClick={() => setPage(page + 1)}
              >
                <ArrowForwardIos />
              </MuiMaterial.IconButton>
            </>
          )}
          {isEndTime && (
            <MuiMaterial.IconButton
              color="error"
              size="large"
              onClick={chooseDifferentTime}
              data-testid={testId && `${testId}.clear-button`}
            >
              <Cancel />
            </MuiMaterial.IconButton>
          )}
        </MuiMaterial.DialogActions>
      </MuiMaterial.Dialog>
    </React.Fragment>
  );
}
